import React, { useState, useEffect, useRef, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';

import env from 'env/env';
import { trackViewShoppableRecipe } from 'analytics/recipes-tracking';
import { RecipeStateItem } from 'redux/modules/recipes/reducers/';
import { GetRecipeType } from 'redux/modules/recipes/selectors';
import { formatRecipeMetaTitle, recipeJsonLd } from 'utils/format-recipe';
import WaitroseLogo from 'components/WaitroseLogo';
import Canonical from 'components/Canonical';
import NotFound from 'components/NotFound/NotFound';
import { getFeatureFlags } from 'utils/feature-flags';
import isEqual from 'lodash/isEqual';
import { scrollIntoViewAndFocusById } from 'utils/scroll';
import AddToList from './Shoppable/AddToList';

import Products from './Shoppable/Products';
import ShoppableHeading from './Shoppable/ShoppableHeading/index';
import { RecipeBreadcrumbs } from './Breadcrumbs';
import Ratings from './Ratings';
import { RecipeDetail } from './RecipeDetail';

import styles from './index.scss';

type RecipesContentProps = {
  recipe: GetRecipeType;
  pageNotFound: boolean;
};

const RecipesContent = ({ recipe, pageNotFound }: RecipesContentProps) => {
  const {
    id,
    title,
    description,
    cookingTime = 0,
    course,
    ingredientGroups = [],
    makes = 0,
    method = [],
    nutritional,
    preparationTime = 0,
    servingSize = 0,
    totalTime = 0,
    images = [],
    loading = false,
    turnOffSEOIndexing = false,
    metaDescription,
    metaKeywords = '',
    firstPublishedDate,
    tags = [],
    products = [],
    rating = { totalRatings: 0 } as RecipeStateItem['rating'],
    isShoppable = false,
  } = recipe;

  /* __SERVER__ is hardcoded to be false */
  /* istanbul ignore next */
  const [showPrint, setShowPrint] = useState(__SERVER__ ? true : !!window.print);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  const ratings = useRef<HTMLDetailsElement>(null);
  const shoppableProductsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowPrint(!!window.print);
  }, [showPrint]);

  const { pathname, search } = useLocation();

  useEffect(() => {
    if (search === '?rating' && ratings.current) ratings.current.focus();
  }, [search, ratings]);

  useEffect(() => {
    if (isShoppable) {
      trackViewShoppableRecipe(title, pathname, rating.loaded ? rating.average : undefined);
    }
  }, [isShoppable, rating]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => {
      if (search === '?shoppableProducts' && shoppableProductsRef.current) {
        // scroll to shoppable products
        scrollIntoViewAndFocusById('shoppableProducts', { top: 0.1 });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/refs
    [search, shoppableProductsRef.current],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleBeforePrint = () => {
      setIsPrinting(true);
    };

    const handleAfterPrint = () => {
      setIsPrinting(false);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  if (pageNotFound) return <NotFound />;

  const descriptionForMeta = metaDescription.substring(0, 160);

  const canonicalHref = `https://${env.prodHost}${pathname}`;

  return (
    <div id="recipe">
      <RecipeBreadcrumbs title={title} />
      <WaitroseLogo small className={styles.logo} />
      <div className={classNames(styles.container, styles.container2)}>
        {!loading && (
          <>
            {/* deepcode ignore DOMXSS - `location.pathname` used to construct canonical URL pathname, reflecting the current URL. React handles attribute DOMXSS. */}
            <Canonical href={canonicalHref} absolute={false} />
            <Helmet title={formatRecipeMetaTitle(title, true, 1)}>
              <meta
                property="og:title"
                content={`${formatRecipeMetaTitle(title, true, 1)} | Waitrose & Partners`}
              />
              <meta name="description" content={descriptionForMeta} />
              <meta property="og:description" content={descriptionForMeta} />
              <meta name="keywords" content={metaKeywords} />
              <meta
                property="og:image"
                content={
                  images[0]?.dynamicImages?.defaultQuality ||
                  `${env.cfDomain}${images[0]?.fileReference}`
                }
              />
            </Helmet>
            {turnOffSEOIndexing && (
              <Helmet>
                <meta data-testid="robots" name="robots" content="noindex" />
              </Helmet>
            )}
            <Helmet>
              <script type="application/ld+json" data-testid="application/ld+json">
                {JSON.stringify(
                  recipeJsonLd(
                    cookingTime,
                    course,
                    description,
                    ingredientGroups,
                    makes,
                    method,
                    nutritional,
                    preparationTime,
                    servingSize,
                    title,
                    totalTime,
                    images,
                    firstPublishedDate,
                    tags,
                    rating,
                  ),
                )}
              </script>
            </Helmet>
          </>
        )}
        <RecipeDetail
          recipe={recipe}
          loading={loading}
          showPrint={showPrint}
          isPrinting={isPrinting}
          rating={rating}
          ratings={ratings}
        />
      </div>
      <div className={styles.container}>
        <div className={classNames(styles.content, styles['recipe-container'])}>
          {loading ? (
            <></>
          ) : (
            <>
              {getFeatureFlags().ShoppablePhase3 && isShoppable && (
                <>
                  <div ref={shoppableProductsRef} id="shoppableProducts">
                    <ShoppableHeading title={title} recipeId={id} />
                    {!loading && (
                      <div className="hidden">
                        <Products products={products} />
                      </div>
                    )}
                  </div>
                </>
              )}
              {!getFeatureFlags().ShoppablePhase3 && isShoppable && (
                <AddToList products={products} />
              )}
            </>
          )}
        </div>
      </div>
      {!loading && isShoppable && !getFeatureFlags().ShoppablePhase3 && (
        <>
          <div ref={shoppableProductsRef} id="shoppableProducts">
            <Products products={products} />
          </div>
        </>
      )}
      <div className="container-fluid">
        <div className={styles.background}>
          <details
            id="rating"
            className={classNames(
              styles.content,
              styles['rating-details'],
              styles['recipe-container'],
            )}
            ref={ratings}
            tabIndex={-1}
            open
          >
            <summary>Rating details</summary>
            <Ratings id={id} ratings={rating} />
          </details>
        </div>
      </div>
    </div>
  );
};

RecipesContent.displayName = 'RecipesPage';

export default memo(RecipesContent, (prevProps, nextProps) => isEqual(prevProps, nextProps));
